import React from "react";
import { Link } from "react-router-dom";
import { GroupLogo } from "@/components/Logo";
import { useEnquiry } from "@/context/EnquiryContext";
import { EXTERNAL_URLS, CONTACT, SOCIAL, waLink } from "@/config/site";
import { Phone, Mail, MessageCircle, ExternalLink, Linkedin, Instagram, Facebook, Youtube, MapPin } from "lucide-react";

const socialIcons = [
  { key: "linkedin", Icon: Linkedin, url: SOCIAL.linkedin, label: "LinkedIn" },
  { key: "instagram", Icon: Instagram, url: SOCIAL.instagram, label: "Instagram" },
  { key: "facebook", Icon: Facebook, url: SOCIAL.facebook, label: "Facebook" },
  { key: "youtube", Icon: Youtube, url: SOCIAL.youtube, label: "YouTube" },
];

export function Footer() {
  const { openEnquiry } = useEnquiry();
  const year = new Date().getFullYear();

  return (
    <footer className="grad-navy text-white" data-testid="main-footer">
      <div className="circuit-grid">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
            {/* Column 1 */}
            <div>
              <GroupLogo className="h-12" withText />
              <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/70">
                Building opportunities through technology, education and social impact.
              </p>
              <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-orange-400">
                Building Skills. Creating Technology. Transforming Lives.
              </p>
            </div>

            {/* Column 2 */}
            <div>
              <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-white">Group</h3>
              <ul className="space-y-3 text-sm text-white/70">
                <li><Link to="/" className="hover:text-orange-400" data-testid="footer-home">Home</Link></li>
                <li><Link to="/about" className="hover:text-orange-400" data-testid="footer-about">About</Link></li>
                <li><Link to="/organizations" className="hover:text-orange-400" data-testid="footer-organizations">Our Organizations</Link></li>
                <li><Link to="/careers" className="hover:text-orange-400" data-testid="footer-careers">Careers</Link></li>
                <li><Link to="/contact" className="hover:text-orange-400" data-testid="footer-contact">Contact</Link></li>
              </ul>
            </div>

            {/* Column 3 */}
            <div>
              <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-white">Our Organizations</h3>
              <ul className="space-y-3 text-sm text-white/70">
                <li><a href={EXTERNAL_URLS.ATS} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-orange-400" data-testid="footer-org-ats">ATS | IT Solutions <ExternalLink className="h-3 w-3" /></a></li>
                <li><a href={EXTERNAL_URLS.ASAI} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-orange-400" data-testid="footer-org-asai">ASAI <ExternalLink className="h-3 w-3" /></a></li>
                <li><a href={EXTERNAL_URLS.AIMT} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-orange-400" data-testid="footer-org-aimt">AIMT <ExternalLink className="h-3 w-3" /></a></li>
                <li><Link to="/foundation" className="hover:text-orange-400" data-testid="footer-org-foundation">Atmasamman Foundation</Link></li>
              </ul>
            </div>

            {/* Column 4 */}
            <div>
              <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-white">Connect</h3>
              <ul className="space-y-3 text-sm text-white/70">
                <li><a href={`tel:${CONTACT.phoneDigits}`} className="inline-flex items-center gap-2 hover:text-orange-400" data-testid="footer-phone"><Phone className="h-4 w-4 text-orange-400" /> {CONTACT.phoneDisplay}</a></li>
                <li><a href={waLink()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-orange-400" data-testid="footer-whatsapp"><MessageCircle className="h-4 w-4 text-orange-400" /> WhatsApp</a></li>
                <li><a href={`mailto:${CONTACT.primaryEmail}`} className="inline-flex items-center gap-2 hover:text-orange-400" data-testid="footer-email"><Mail className="h-4 w-4 text-orange-400" /> {CONTACT.primaryEmail}</a></li>
                <li className="inline-flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-orange-400" /> <span>{CONTACT.addressShort}</span></li>
              </ul>

              <div className="mt-5 flex items-center gap-3">
                {socialIcons.filter((s) => s.url).map(({ key, Icon, url, label }) => (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={`footer-social-${key}`}
                     className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 text-white/80 transition-colors hover:border-orange-500 hover:bg-orange-500 hover:text-white">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>

              <button onClick={() => openEnquiry({ interest: "Atmasamman Group" })} data-testid="footer-request-info-btn"
                className="mt-5 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-orange-600 hover:-translate-y-0.5">
                Request Information
              </button>
            </div>
          </div>

          <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-6 text-xs text-white/60 sm:flex-row">
            <p>© {year} Atmasamman Group. All Rights Reserved.</p>
            <div className="flex items-center gap-5">
              <Link to="/privacy-policy" className="hover:text-orange-400" data-testid="footer-privacy">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-orange-400" data-testid="footer-terms">Terms & Conditions</Link>
              <Link to="/cookie-policy" className="hover:text-orange-400" data-testid="footer-cookie">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
